import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PenSquare, Download, AlertTriangle, FileCheck2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { AgenteIaContextoSeletor, type AgenteIaContexto } from '@/components/agentes-ia/AgenteIaContextoSeletor';
import { useAgenteIaJob } from '@/hooks/useAgenteIaJob';
import { useDocumentosGerados } from '@/hooks/useRedatorNinja';
import { useViewModeStore } from '@/stores/view-mode';
import type { TipoDocumento, FaseDisputa } from '@/types/agentesIa';

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-ds-text-secondary italic text-center py-8">{message}</p>;
}

const TIPO_OPTIONS: { value: TipoDocumento; label: string }[] = [
  { value: 'declaracao', label: 'Declaração' },
  { value: 'pedido_esclarecimento', label: 'Pedido de esclarecimento' },
  { value: 'impugnacao', label: 'Impugnação' },
  { value: 'recurso', label: 'Recurso' },
  { value: 'contrarrazao', label: 'Contrarrazão' },
];

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-brand/50 focus:border-ds-brand/50';

function formatDate(value: string) {
  return new Date(value).toLocaleString('pt-BR');
}

function RedatorNinja() {
  const [searchParams] = useSearchParams();
  const initialEmpresaId = Number(searchParams.get('empresaId')) || undefined;
  const initialLicitacaoId = Number(searchParams.get('licitacaoId')) || undefined;
  const tipoInicial = searchParams.get('tipo');
  const { mode, previewEmpresaId } = useViewModeStore();
  const isEmpresaContextual = mode === 'empresa' && initialEmpresaId === previewEmpresaId && Boolean(initialLicitacaoId);
  const isAdministrativeAction = isEmpresaContextual && TIPO_OPTIONS.some((option) => option.value === tipoInicial);
  const [contexto, setContexto] = useState<AgenteIaContexto | null>(null);
  const [tipo, setTipo] = useState<TipoDocumento>(
    TIPO_OPTIONS.some((option) => option.value === tipoInicial) ? tipoInicial as TipoDocumento : 'declaracao'
  );
  const [fase, setFase] = useState<FaseDisputa | ''>('');

  useEffect(() => {
    if (TIPO_OPTIONS.some((option) => option.value === tipoInicial)) setTipo(tipoInicial as TipoDocumento);
  }, [tipoInicial]);

  const { enqueue, isEnqueuing, jobStatus, reset } = useAgenteIaJob('redator-ninja', contexto?.empresaId);
  const { data: documentos, isLoading: documentosLoading } = useDocumentosGerados(
    contexto?.empresaId,
    contexto?.licitacaoId,
    jobStatus?.status
  );

  const isRunning = Boolean(jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed');

  const handleGerar = async () => {
    if (!contexto) return;
    reset();
    await enqueue({
      licitacaoId: contexto.licitacaoId,
      documentoId: contexto.documentoId,
      tipo,
      fase: fase || undefined,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ds-text-primary flex items-center gap-2">
          <PenSquare className="h-6 w-6 text-ds-brand" />
          {isAdministrativeAction ? (tipo === 'impugnacao' ? 'Impugnar edital' : 'Pedir esclarecimento') : 'Redator Ninja'}
        </h1>
        <p className="text-ds-text-secondary mt-1">
          {isAdministrativeAction ? 'Prepare um rascunho para revisão humana da licitação selecionada.' : 'Prepare um rascunho de declaração, impugnação, recurso ou contrarrazão a partir do edital.'}
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-ds-warning/30 bg-ds-warning/10 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-ds-warning" />
        <div>
          <p className="font-semibold text-ds-text-primary">Rascunho para revisão humana</p>
          <p className="mt-1 text-sm text-ds-text-secondary">O Redator organiza uma minuta com base no edital e nos dados disponíveis. Revise o conteúdo antes de assinar ou protocolar.</p>
        </div>
      </div>

      <AgenteIaContextoSeletor
        onContextoCompleto={setContexto}
        initialEmpresaId={initialEmpresaId}
        initialLicitacaoId={initialLicitacaoId}
        contextoTravado={isEmpresaContextual}
        empresaRestritaId={mode === 'empresa' ? previewEmpresaId ?? undefined : undefined}
      />

      <Card>
        <CardHeader>
          <CardTitle>Preparar rascunho em PDF</CardTitle>
          <CardDescription>Escolha o tipo de peça e o edital. O arquivo será disponibilizado em PDF para revisão humana.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isAdministrativeAction && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="redator-tipo">
                Tipo de documento
              </label>
              <select
                id="redator-tipo"
                className={selectClassName}
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoDocumento)}
              >
                {TIPO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="redator-fase">
                Fase (opcional)
              </label>
              <select
                id="redator-fase"
                className={selectClassName}
                value={fase}
                onChange={(e) => setFase(e.target.value as FaseDisputa | '')}
              >
                <option value="">Não especificar</option>
                <option value="pre_disputa">Pré-disputa</option>
                <option value="pos_disputa">Pós-disputa</option>
              </select>
            </div>
          </div>
          }

          <Button
            onClick={handleGerar}
            disabled={!contexto || isEnqueuing || isRunning}
            isLoading={isEnqueuing || isRunning}
          >
            {isEnqueuing || isRunning ? 'Gerando rascunho...' : isAdministrativeAction ? `Gerar rascunho de ${tipo === 'impugnacao' ? 'impugnação' : 'esclarecimento'}` : 'Gerar rascunho em PDF'}
          </Button>

          {jobStatus?.status === 'failed' && (
            <div className="flex items-start gap-3 rounded-lg border border-ds-danger/20 bg-ds-danger/10 p-4">
              <AlertTriangle className="h-5 w-5 text-ds-danger shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ds-danger">Falha ao gerar documento</p>
                <p className="text-sm text-ds-text-secondary mt-1">{jobStatus.failedReason}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rascunhos gerados</CardTitle>
          <CardDescription>Versões da licitação selecionada, mais recente primeiro. PDFs novos mantêm estrutura pronta para revisão.</CardDescription>
        </CardHeader>
        <CardContent>
          {!contexto ? (
            <EmptyState message="Selecione empresa, licitação e documento para ver os documentos gerados." />
          ) : documentosLoading ? (
            <p className="text-center text-ds-text-secondary py-8">Carregando...</p>
          ) : !documentos || documentos.length === 0 ? (
            <EmptyState message="Nenhum documento gerado ainda para esta licitação." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Gerado em</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentos.map((documento) => (
                  <TableRow key={documento.id}>
                    <TableCell>{TIPO_OPTIONS.find((o) => o.value === documento.tipo)?.label || documento.tipo}</TableCell>
                    <TableCell>{formatDate(documento.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(documento.signedUrl, '_blank', 'noopener,noreferrer')}
                      >
                        {documento.formato === 'pdf' ? <FileCheck2 className="h-4 w-4 mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                        {documento.formato === 'pdf' ? 'Baixar PDF' : 'Baixar rascunho'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { RedatorNinja };
